import { keepPreviousData } from "@tanstack/react-query";
import { WaterfallMaxPrefBanner } from "features/waterfall/review/WaterfallMaxPrefBanner";
import { WaterfallPreferredReturnStats } from "features/waterfall/review/WaterfallPreferredReturnStats";
import {
  useWaterfallPreferredReturnBreakdownTableDataRetrieve,
  useWaterfallPreferredReturnSummaryDataRetrieveSuspense,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { useGlobalEntityRetrieveSuspense } from "gen/endpoints/global-entity/global-entity";
import { useInvestorEntityRetrieveSuspense } from "gen/endpoints/investor-entity/investor-entity";
import { GlobalEntityData } from "gen/model/globalEntityData";
import { InvestorEntityData } from "gen/model/investorEntityData";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingState } from "shared/components/empty/LoadingState";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

const WaterfallReviewPreferredReturn: React.FC<{
  fundFamilyEventId: string;
  fundFamilyId: string;
  fundFamilySubEventId: string;
  fundEntityId: string;
  investorEntityId?: string | undefined;
  entityData: InvestorEntityData | GlobalEntityData;
}> = ({
  fundFamilyEventId,
  fundFamilyId,
  fundFamilySubEventId,
  fundEntityId,
  investorEntityId,
  entityData,
}) => {
  const { t } = useTranslation(["common", "distribution", "waterfall"]);
  const [isEqualizedView, setIsEqualizedView] = useState<boolean>(false);

  const { data: summaryData } =
    useWaterfallPreferredReturnSummaryDataRetrieveSuspense(
      fundFamilyId,
      fundFamilyEventId,
      fundFamilySubEventId,
      fundEntityId,
      {
        investor_entity_id: investorEntityId,
      },
    );

  const { data, isFetching } =
    useWaterfallPreferredReturnBreakdownTableDataRetrieve(
      fundFamilyId,
      fundFamilyEventId,
      fundFamilySubEventId,
      fundEntityId,
      {
        investor_entity_id: investorEntityId,
        is_equalized_view: isEqualizedView || false,
      },
      {
        query: {
          placeholderData: keepPreviousData,
        },
      },
    );

  const baseRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .capitalActivity({})
    .capitalActivityEvent({ fundFamilyEventId })
    .waterfall({})
    .review({ fundFamilySubEventId, fundEntityId });

  return (
    <PageContent>
      <PageHeader
        backButton={{
          title: investorEntityId
            ? t("waterfall:preferredReturnReview.fundLevel.back")
            : t("waterfall:preferredReturnReview.investorLevel.back"),
          // If it's an entity page, go back to the entity breakdown page
          path: investorEntityId
            ? baseRoute.breakdown({ investorEntityId }).investor({}).$
            : // Otherwise, go back to the main review page
              baseRoute.$,
        }}
        title={t("waterfall:preferredReturnReview.title")}
        badge={{
          type: "default",
          text: entityData.legal_name,
        }}
        stats={
          <WaterfallPreferredReturnStats
            data={summaryData}
            isEqualizedView={isEqualizedView}
            setIsEqualizedView={setIsEqualizedView}
          />
        }
      />
      <PageBody>
        {summaryData.fund_family_subevent_with_max_preferred_return !==
          fundFamilySubEventId && (
          <WaterfallMaxPrefBanner
            summaryData={summaryData}
            fundFamilyId={fundFamilyId}
            fundFamilyEventId={fundFamilyEventId}
            fundEntityId={fundEntityId}
            investorEntityId={investorEntityId}
          />
        )}
        <TableBox isFullWidth={true}>
          <TableBoxHeader>
            <TableBoxTitle>
              {t("waterfall:preferredReturnReview.tableTitle")}
            </TableBoxTitle>
            <DataTableBoxActions />;
          </TableBoxHeader>
          <TableBoxBody>
            {data ? (
              <DataTable data={data} isLoading={isFetching} />
            ) : (
              <LoadingState />
            )}
          </TableBoxBody>
        </TableBox>
      </PageBody>
    </PageContent>
  );
};

export const WaterfallReviewPreferredReturnFundPage: React.FC = () => {
  const {
    fundFamilyEventId,
    fundFamilyId,
    fundFamilySubEventId,
    fundEntityId,
  } = useRequiredStringParams();

  const { data: summaryData } =
    useWaterfallPreferredReturnSummaryDataRetrieveSuspense(
      fundFamilyId,
      fundFamilyEventId,
      fundFamilySubEventId,
      fundEntityId,
    );

  const { data: fundEntityData } = useGlobalEntityRetrieveSuspense(
    summaryData.fund_id,
  );

  return (
    <WaterfallReviewPreferredReturn
      fundFamilyEventId={fundFamilyEventId}
      fundFamilyId={fundFamilyId}
      fundFamilySubEventId={fundFamilySubEventId}
      fundEntityId={fundEntityId}
      entityData={fundEntityData}
    />
  );
};

export const WaterfallReviewPreferredReturnInvestorPage: React.FC = () => {
  const {
    fundFamilyEventId,
    fundFamilyId,
    fundFamilySubEventId,
    fundEntityId,
    investorEntityId,
  } = useRequiredStringParams();

  const { data: investorEntityData } =
    useInvestorEntityRetrieveSuspense(investorEntityId);

  return (
    <WaterfallReviewPreferredReturn
      fundFamilyEventId={fundFamilyEventId}
      fundFamilyId={fundFamilyId}
      fundFamilySubEventId={fundFamilySubEventId}
      fundEntityId={fundEntityId}
      investorEntityId={investorEntityId}
      entityData={investorEntityData}
    />
  );
};
